import * as React from 'react'
import { DotsThreeVertical } from '@phosphor-icons/react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'

// Shared Edit / Remove kebab menu used by BOTH check-in questions and
// notifications so the two stay pixel-identical (and reusable by check-in cycles
// later). Radix gives keyboard navigation, focus management and outside-click
// dismissal for free.
//
// Figma: white card, rounded-2xl, soft ambient shadow, p-2; items are
// rounded-lg, TT Talent 16px/20px Medium — "Edit" in navy (indigo-950) and
// "Remove" in red. No icons.
export function EditRemoveMenu({
  triggerLabel,
  onEdit,
  onRemove,
}: {
  /** Accessible label for the kebab trigger button, e.g. "Question options". */
  triggerLabel: string
  onEdit: () => void
  /** Omit to render an edit-only menu — e.g. seeded default reminders that can
   * be turned off but not deleted. */
  onRemove?: () => void
}) {
  // `modal={false}`: a kebab menu shouldn't lock the page behind it, and more
  // importantly the default modal mode sets `pointer-events: none` on
  // document.body. When a menu item opens a confirmation Dialog (also modal),
  // the dropdown's deferred lock-restore races with the dialog's, and on close
  // the body is left stuck at `pointer-events: none` — freezing the whole page.
  // Non-modal removes the dropdown's body lock while keeping outside-click
  // dismissal and keyboard nav.
  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={triggerLabel}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-indigo-950"
        >
          <DotsThreeVertical className="h-6 w-6" weight="bold" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-50 flex min-w-[160px] flex-col rounded-2xl bg-white p-2 shadow-[0px_8px_24px_-6px_rgba(15,20,30,0.16),0px_2px_6px_-2px_rgba(15,20,30,0.08)]"
          align="end"
          sideOffset={6}
        >
          <DropdownMenu.Item
            className="cursor-pointer rounded-lg px-4 py-2.5 font-talent text-base font-medium leading-5 text-indigo-950 outline-none hover:bg-gray-50"
            onSelect={onEdit}
          >
            Edit
          </DropdownMenu.Item>
          {onRemove && (
            <DropdownMenu.Item
              className="cursor-pointer rounded-lg px-4 py-2.5 font-talent text-base font-medium leading-5 text-red-600 outline-none hover:bg-gray-50"
              onSelect={onRemove}
            >
              Remove
            </DropdownMenu.Item>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
